import { createMemo, For, Show, type JSX } from "solid-js";

import {
  patientPearlShapes,
  PearlShapes,
  type PatientPearlCategory,
} from "~/charting/xrays_and_imaging/pearl_annotations";
import type { PearlAnnotation, PearlResult } from "~/charting/xrays_and_imaging/pearl_types";
import { SEPARATOR_DOT } from "~/util/html/codepoint";

import styles from "./deck.module.css";
import { DeckImageFigure } from "./deck_image_figure";
import { useDeckWords, type DeckLabelKey } from "./deck_words";
import type { DeckPicture, PageFrame, PictureNote } from "./types";

type PatientPearl = Pick<PearlResult, "width" | "height" | "annotations">;

/** What each mark color means, in the patient's words (deck_words). */
const PEARL_LEGEND_KEYS: Record<PatientPearlCategory, DeckLabelKey> = {
  caries_progressed: "decay",
  caries_incipient: "earlyDecay",
  calculus: "tartar",
  periapical_radiolucency: "rootTipSpot",
  notable_margin: "fillingEdge",
};

/** A picture's Pearl marks, or null when it has none or its size or mark list is malformed. A malformed mark is
 *  left out on its own, so it never takes the picture's good marks with it. */
function parsePatientPearl(json: string | null): PatientPearl | null {
  if (json === null) return null;
  let value: unknown;
  try {
    value = JSON.parse(json);
  } catch {
    return null;
  }
  if (!isFields(value)) return null;
  const width = value["width"];
  const height = value["height"];
  const annotations = value["annotations"];
  if (!isPositiveNumber(width) || !isPositiveNumber(height) || !Array.isArray(annotations)) {
    return null;
  }
  return { width, height, annotations: annotations.map(withoutNulls).filter(isDrawableAnnotation) };
}

type Fields = Record<string, unknown>;

function isFields(value: unknown): value is Fields {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isPositiveNumber(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0;
}

function hasNumbers(value: unknown, keys: readonly string[]): boolean {
  return isFields(value) && keys.every((key) => isFiniteNumber(value[key]));
}

// Pearl can send null for a mark with no fill or no relationships. PearlShapes reads either as none, and the
// parsed copy says undefined, as PearlAnnotation's type does.
function withoutNulls(value: unknown): unknown {
  if (!isFields(value)) return value;
  return {
    ...value,
    fill_color: value["fill_color"] ?? undefined,
    relationships: value["relationships"] ?? undefined,
  };
}

// Checks every field PearlShapes and patientPearlShapes read, so a bad mark is left out instead of throwing
// in the middle of a patient's deck.
function isDrawableAnnotation(value: unknown): value is PearlAnnotation {
  if (!isFields(value)) return false;
  const polygon = value["polygon"];
  const relationships = value["relationships"];
  const endpoints = value["endpoints"];
  const text = value["text"];
  const fillColor = value["fill_color"];
  return (
    typeof value["category"] === "string" &&
    isFields(value["line_segment"]) &&
    Array.isArray(polygon) &&
    polygon.every((point) => hasNumbers(point, ["x", "y"])) &&
    hasNumbers(value["contour_box"], ["x", "y", "width", "height"]) &&
    typeof value["stroke_color"] === "string" &&
    isFiniteNumber(value["stroke_width"]) &&
    (fillColor === undefined || typeof fillColor === "string") &&
    (relationships === undefined ||
      (Array.isArray(relationships) &&
        relationships.every(
          (relationship) =>
            isFields(relationship) &&
            typeof relationship["prop_value"] === "string" &&
            isFiniteNumber(relationship["metric_value"]),
        ))) &&
    (endpoints === undefined ||
      endpoints === null ||
      (Array.isArray(endpoints) &&
        endpoints.every((endpoint) => hasNumbers(endpoint, ["x", "y", "radius"])))) &&
    (text === undefined ||
      text === null ||
      (isFields(text) && hasNumbers(text, ["x", "y"]) && typeof text["text"] === "string"))
  );
}

/** The marks of Pearl's a patient sees on a picture, with what each color means, or null when Pearl marked nothing a
 *  patient sees (or never read it). */
export function patientMarks(picture: Pick<DeckPicture, "pearlJson">) {
  const pearl = parsePatientPearl(picture.pearlJson);
  if (pearl === null) return null;
  const shapes = patientPearlShapes(pearl);
  return shapes.annotations.length > 0 ? { pearl, ...shapes } : null;
}

/** Where a pointer sits in the figure, as shares of it, for a picture shown whole or framed; null when the frame
 *  leaves it out. */
export function pointInFrame(
  note: PictureNote,
  frame: PageFrame | undefined,
): { x: number; y: number } | null {
  const part = frame ?? { x: 0, y: 0, width: 1, height: 1 };
  const x = (note.x - part.x) / part.width;
  const y = (note.y - part.y) / part.height;
  return x < 0 || x > 1 || y < 0 || y > 1 ? null : { x, y };
}

// Past this share of the figure's width, a pointer's words sit to its left so they stay on the picture.
const NOTE_FLIP = 0.62;

/** A deck picture with Pearl's marks over it and a legend under it, and staff's pointers. The pixels arrive oriented,
 *  so the marks line up with no transform. `failedText`, `large` and `frame` are the figure's (DeckImageFigure);
 *  `marks` false leaves Pearl's marks and their legend off. */
export function PictureWithMarks(props: {
  picture: DeckPicture;
  src: string;
  failedText?: string;
  large?: boolean;
  frame?: PageFrame;
  marks?: boolean;
  notes?: PictureNote[];
  /** Staff's words under the picture. */
  words?: string;
}): JSX.Element {
  const words = useDeckWords();
  // Nothing a patient sees: no overlay and no legend.
  const marks = createMemo(() => (props.marks === false ? null : patientMarks(props.picture)));

  return (
    <DeckImageFigure
      src={props.src}
      alt={props.picture.label}
      width={props.picture.width}
      height={props.picture.height}
      failedText={props.failedText ?? words().t("pictureUnavailable")}
      large={props.large}
      frame={props.frame}
      words={props.words}
      overlay={
        <>
          <Show when={marks()}>
            {(drawn) => (
              <PearlShapes
                class={`pointer-events-none absolute inset-0 size-full ${styles["pic-over"]}`}
                data={drawn().pearl}
                annotations={drawn().annotations}
                toothParts={[]}
                imagePxPerScreenPx={drawn().pearl.width / 600}
                nonScalingStroke
              />
            )}
          </Show>
          <Show when={props.notes?.length}>
            <ul class={styles["pic-notes"]} aria-label="Pointers">
              <For each={props.notes}>
                {(note) => (
                  <Show when={pointInFrame(note, props.large ? props.frame : undefined)}>
                    {(at) => (
                      <li
                        class={styles["pic-note"]}
                        data-flip={at().x > NOTE_FLIP ? "" : undefined}
                        style={{ left: `${at().x * 100}%`, top: `${at().y * 100}%` }}
                      >
                        <span>{note.text}</span>
                      </li>
                    )}
                  </Show>
                )}
              </For>
            </ul>
          </Show>
        </>
      }
      legend={
        <Show when={marks()?.findings.length}>
          <ul class={styles["legend"]} aria-label="What the marks show">
            <For each={marks()?.findings}>
              {(finding) => (
                <li>
                  <span class={styles["legend-dot"]} style={{ background: finding.swatch }} />
                  {words().t(PEARL_LEGEND_KEYS[finding.category])}
                  <Show when={finding.count > 1}> ×{finding.count}</Show>
                </li>
              )}
            </For>
          </ul>
        </Show>
      }
      caption={
        <>
          {words().t(props.picture.kind === "xray" ? "xRay" : "photo")} {SEPARATOR_DOT}{" "}
          {words().date(props.picture.takenOn)}
        </>
      }
    />
  );
}
